import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import './App.css';

// Importation des composants
import Header from './components/Header';
import Calendar from './components/Calendar';
import DailyView from './components/DailyView';
import SessionDetails from './components/SessionDetails';
import AddParticipant from './components/AddParticipant';
import Footer from './components/Footer';

// Importation des services
import {
  getSessions,
  getParticipants,
  recalculateAllSessionCounters,
  initializeData,
  createSession,
  addParticipant,
  updateParticipant,
  deleteParticipant,
  blockSession,
  unblockSession,
  deleteSession,
  reopenSession  // ← AJOUTER cette ligne
} from './services/dataService';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from "./firebase";

function App() {
  // États pour stocker les données
  const [sessions, setSessions] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminPrompt, setShowAdminPrompt] = useState(false);
  const [adminInput, setAdminInput] = useState('');

  // Configuration des horaires disponibles de base
  const baseHours = [
    '10:00', '10:30', '11:00', '11:30',
    '14:00', '14:30', '15:00', '15:30',
    '16:00', '16:30', '17:00'
  ];

  // Fonction pour vérifier si une date est dans la saison (1er juillet au 31 août)
  const isInSeason = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth(); // 0 = janvier, 5 = juin
    const day = date.getDate();
    const dayOfWeek = date.getDay(); // 0 = dimanche, 3 = mercredi, 6 = samedi

    // Juillet et août : tous les jours ouverts
    if (month === 6 || month === 7) {
      return true;
    }

    // Juin
    if (month === 5) {
      if (day < 14) {
        // Avant le 14 juin : samedi (6) et dimanche (0)
        return dayOfWeek === 6 || dayOfWeek === 0;
      } else {
        // À partir du 14 juin : mercredi (3), samedi (6), dimanche (0)
        return dayOfWeek === 3 || dayOfWeek === 6 || dayOfWeek === 0;
      }
    }

    // Autres mois : adapte selon ta règle (exemple : samedi/dimanche)
    return dayOfWeek === 6 || dayOfWeek === 0;
  };

  // Fonction pour obtenir les horaires selon la date
  const getAvailableHours = (date) => {
    const isJune = date.getMonth() === 5;
    const dayOfWeek = date.getDay();
    const isJuneAllowedDay = isJune && (dayOfWeek === 0 || dayOfWeek === 3 || dayOfWeek === 6);

    let hours = [...baseHours];
    if (
      (isJune && isJuneAllowedDay) ||
      date.getMonth() === 6 || // juillet
      date.getMonth() === 7    // août
    ) {
      // Ajouter les créneaux étendus
      if (!hours.includes('17:30')) hours.push('17:30');
      if (!hours.includes('18:00')) hours.push('18:00');
      if (!hours.includes('18:30')) hours.push('18:30'); // ← NOUVEAU
      if (!hours.includes('19:00')) hours.push('19:00');  // ← NOUVEAU
      if (!hours.includes('19:30')) hours.push('19:30');  // ← NOUVEAU
      if (!hours.includes('20:00')) hours.push('20:00');  // ← NOUVEAU
      if (!hours.includes('20:30')) hours.push('20:30');  // ← NOUVEAU
      if (!hours.includes('21:00')) hours.push('21:00');  // ← NOUVEAU
      if (!hours.includes('21:30')) hours.push('21:30');  // ← NOUVEAU
      
      // Trier par ordre chronologique
      hours = hours.sort((a, b) => {
        const [ah, am] = a.split(':').map(Number);
        const [bh, bm] = b.split(':').map(Number);
        return ah !== bh ? ah - bh : am - bm;
      });
    }
    return hours;
  };

  // Initialisation des données au démarrage
  useEffect(() => {
    const initialize = async () => {
      try {
        await initializeData();
        setInitialized(true);
      } catch (err) {
        console.error("Erreur lors de l'initialisation:", err);
        setError("Erreur lors de l'initialisation des données");
      }
    };

    initialize();
  }, []);

  // Chargement des données
  useEffect(() => {
    const loadData = async () => {
      if (!initialized) return;

      try {
        setLoading(true);
        const sessionsData = await getSessions();
        const participantsData = await getParticipants();
        setSessions(sessionsData);
        setParticipants(participantsData);
      } catch (err) {
        setError('Erreur lors du chargement des données: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [initialized]);

  // Fonction pour sélectionner une date
  const handleDateChange = (date) => {
    setCurrentDate(date);
    setSelectedSession(null);
  };

  // Fonction pour sélectionner une session
  const handleSessionSelect = (session) => {
    setSelectedSession(session);
  };

  // Créer une session
  const handleCreateSession = async (sessionData) => {
    try {
      const newSession = await createSession(sessionData);
      setSessions(prevSessions => [...prevSessions, newSession]);
      setSelectedSession(newSession);
      return newSession;
    } catch (err) {
      setError('Erreur lors de la création de la session: ' + err.message);
      return null;
    }
  };

  // Ajouter un participant
  const handleAddParticipant = async (participantData) => {
    try {
      const newParticipant = await addParticipant({
        ...participantData,
        sessionId: selectedSession.id
      });

      setParticipants([...participants, newParticipant]);

      // Mettre à jour le nombre de participants dans la session
      const updatedSessions = sessions.map(s =>
        s.id === selectedSession.id
          ? {
              ...s,
              participantCount: (s.participantCount || 0) + 1,
              paidCount: participantData.paid ? (s.paidCount || 0) + 1 : (s.paidCount || 0)
            }
          : s
      );
      setSessions(updatedSessions);

      // Mettre à jour aussi la session sélectionnée
      setSelectedSession(prev => ({
        ...prev,
        participantCount: (prev.participantCount || 0) + 1,
        paidCount: participantData.paid ? (prev.paidCount || 0) + 1 : (prev.paidCount || 0)
      }));

      return true;
    } catch (err) {
      setError('Erreur lors de l\'ajout du participant: ' + err.message);
      return false;
    }
  };

  // Mettre à jour le statut de paiement d'un participant
  const handleUpdatePayment = async (participantId, isPaid) => {
    try {
      await updateParticipant(participantId, { paid: isPaid });

      const updatedParticipants = participants.map(p =>
        p.id === participantId ? { ...p, paid: isPaid } : p
      );
      setParticipants(updatedParticipants);

      // Mettre à jour le nombre de paiements dans la session
      const participant = participants.find(p => p.id === participantId);
      if (participant) {
        const sessionId = participant.sessionId;
        const updatedSessions = sessions.map(s =>
          s.id === sessionId
            ? {
                ...s,
                paidCount: isPaid
                  ? (s.paidCount || 0) + 1
                  : Math.max(0, (s.paidCount || 0) - 1)
              }
            : s
        );
        setSessions(updatedSessions);

        // Mettre à jour aussi la session sélectionnée si c'est la même
        if (selectedSession && selectedSession.id === sessionId) {
          setSelectedSession(prev => ({
            ...prev,
            paidCount: isPaid
              ? (prev.paidCount || 0) + 1
              : Math.max(0, (prev.paidCount || 0) - 1)
          }));
        }
      }

      return true;
    } catch (err) {
      setError('Erreur lors de la mise à jour du paiement: ' + err.message);
      return false;
    }
  };

  // Supprimer un participant
  const handleDeleteParticipant = async (participantId) => {
    try {
      await deleteParticipant(participantId);

      // Trouver à quelle session appartient le participant
      const participant = participants.find(p => p.id === participantId);
      const sessionId = participant?.sessionId;

      setParticipants(participants.filter(p => p.id !== participantId));

      // Mettre à jour le nombre de participants dans la session
      if (sessionId) {
        const updatedSessions = sessions.map(s =>
          s.id === sessionId
            ? {
                ...s,
                participantCount: Math.max(0, (s.participantCount || 0) - 1),
                paidCount: participant.paid ? Math.max(0, (s.paidCount || 0) - 1) : s.paidCount
              }
            : s
        );
        setSessions(updatedSessions);

        // Mettre à jour aussi la session sélectionnée si c'est la même
        if (selectedSession && selectedSession.id === sessionId) {
          setSelectedSession(prev => ({
            ...prev,
            participantCount: Math.max(0, (prev.participantCount || 0) - 1),
            paidCount: participant.paid ? Math.max(0, (prev.paidCount || 0) - 1) : prev.paidCount
          }));
        }
      }

      return true;
    } catch (err) {
      setError('Erreur lors de la suppression du participant: ' + err.message);
      return false;
    }
  };

  // Bloquer une session
  const handleBlockSession = async (sessionId, reason, isGroupReservation = false, groupData = null) => {
    try {
      const success = await blockSession(sessionId, reason, isGroupReservation, groupData);
      if (success) {
        // Met à jour la liste des sessions
        const updatedSessions = sessions.map(s =>
          s.id === sessionId
            ? { 
                ...s, 
                isBlocked: true, 
                blockReason: reason,
                blockType: isGroupReservation ? 'group' : 'admin',
                groupReservationData: isGroupReservation && groupData ? groupData : null,
                participantCount: isGroupReservation && groupData ? (groupData.playerCount || 1) : 0,
                paidCount: isGroupReservation && groupData && groupData.paid ? (groupData.playerCount || 1) : 0
              }
          : s
        );
        setSessions(updatedSessions);

        // Met à jour la session sélectionnée si besoin
        if (selectedSession && selectedSession.id === sessionId) {
          setSelectedSession(prev => ({
            ...prev,
            isBlocked: true,
            blockReason: reason,
            blockType: isGroupReservation ? 'group' : 'admin',
            groupReservationData: isGroupReservation && groupData ? groupData : null,
            participantCount: isGroupReservation && groupData ? (groupData.playerCount || 1) : 0,
            paidCount: isGroupReservation && groupData && groupData.paid ? (groupData.playerCount || 1) : 0
          }));
        }
      }
      return success;
    } catch (err) {
      setError('Erreur lors du blocage de la session: ' + err.message);
      return false;
    }
  };

  // Débloquer une session
  const handleUnblockSession = async (sessionId) => {
    // Appelle ton service pour débloquer la session
    await unblockSession(sessionId);
    // Mets à jour l'état local si besoin
    setSessions(sessions =>
      sessions.map(s =>
        s.id === sessionId ? { ...s, isBlocked: false, blockReason: '' } : s
      )
    );
  };

  // Supprimer une session (créneau) - maintenant "fermer"
  const handleDeleteSession = async (sessionId) => {
    try {
      // Vérifie qu'il n'y a pas de participants inscrits
      const session = sessions.find(s => s.id === sessionId);
      if (session && session.participantCount > 0) {
        setError("Impossible de fermer une session avec des participants inscrits.");
        return false;
      }

      // Marquer la session comme fermée (au lieu de la supprimer)
      await deleteSession(sessionId);

      // Mettre à jour l'état local - marquer comme fermée
      setSessions(sessions.map(s => 
        s.id === sessionId 
          ? { ...s, isDeleted: true, participantCount: 0, paidCount: 0 }
          : s
      ));
      
      if (selectedSession && selectedSession.id === sessionId) {
        setSelectedSession(null);
      }
      return true;
    } catch (err) {
      setError("Erreur lors de la fermeture de la session: " + err.message);
      return false;
    }
  };

  // Nouvelle fonction pour réouvrir une session
  const handleReopenSession = async (sessionId) => {
    try {
      await reopenSession(sessionId);
      
      // Mettre à jour l'état local
      setSessions(sessions.map(s => 
        s.id === sessionId 
          ? { ...s, isDeleted: false }
          : s
      ));
      
      return true;
    } catch (err) {
      setError("Erreur lors de la réouverture: " + err.message);
      return false;
    }
  };

  // Exporter tous les participants au format CSV
  const exportAllParticipantsCSV = () => {
    if (!participants.length) return;
    const header = ['Nom', 'Téléphone', 'Email', 'Joueurs', 'Payé', 'Session', 'Date', 'Heure'];
    const rows = participants.map(p => {
      const session = sessions.find(s => s.id === p.sessionId) || {};
      return [
        `"${p.name || ''}"`,
        `"${p.phone || ''}"`,
        `"${p.email || ''}"`,
        p.playerCount || 1,
        p.paid ? 'Oui' : 'Non',
        `"${session.id || ''}"`,
        `"${session.date || ''}"`,
        `"${session.time || ''}"`
      ].join(',');
    });
    const csvContent = [header.join(','), ...rows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'participants-tous.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fonction pour afficher le prompt admin
  const handleAdminClick = () => {
    setShowAdminPrompt(true);
    setAdminInput('');
  };

  // Fonction pour vérifier le mot de passe admin
  const handleAdminSubmit = (e) => {
    e.preventDefault();
    if (adminInput === 'Equestre1236') {
      setIsAdmin(true);
      setShowAdminPrompt(false);
    } else {
      alert('Mot de passe incorrect');
    }
  };

  // Fonction pour désactiver le mode admin
  const handleAdminLogout = () => {
    setIsAdmin(false);
  };

  // Recalculer les compteurs de toutes les sessions et forcer le refresh
  const handleRecalculateCounters = async () => {
    if (!window.confirm("Voulez-vous vraiment recalculer tous les compteurs ? Cette opération peut prendre quelques secondes.")) {
      return;
    }

    console.log("🔄 Début du recalcul des compteurs...");
    setLoading(true);
    setError(null);
    
    try {
      // 1. Recalculer les compteurs dans Firebase (même si tout est OK, pour être sûr)
      console.log("📊 Vérification et recalcul des compteurs Firebase...");
      const result = await recalculateAllSessionCounters();
      console.log("✅ Vérification Firebase terminée:", result);
      
      // 2. FORCER le rechargement complet des données
      console.log("🔄 Rechargement forcé des données...");
      setSessions([]);
      setParticipants([]);
      setSelectedSession(null);
      await new Promise(resolve => setTimeout(resolve, 200));
      const [freshSessions, freshParticipants] = await Promise.all([
        getSessions(),
        getParticipants()
      ]);
      setSessions(freshSessions);
      setParticipants(freshParticipants);
      console.log("✅ Refresh complet terminé");
      if (result.updatedSessions > 0) {
        alert(`✅ Recalcul terminé !\n\n📊 ${result.updatedSessions} sessions corrigées\n🔄 Affichage mis à jour`);
      } else {
        alert(`✅ Vérification terminée !\n\n📊 Tous les compteurs étaient déjà corrects\n🔄 Affichage actualisé`);
      }
    } catch (err) {
      console.error("❌ Erreur lors du recalcul:", err);
      setError('Erreur lors du recalcul des compteurs: ' + err.message);
      alert('❌ Erreur lors du recalcul des compteurs.\nVérifiez la console pour plus de détails.');
    } finally {
      setLoading(false);
    }
  };

  // Actualisation simple de l'affichage
  const handleForceRefresh = async () => {
    console.log("🔄 Actualisation forcée...");
    setLoading(true);
    try {
      setSessions([]);
      setParticipants([]);
      setSelectedSession(null);
      await new Promise(resolve => setTimeout(resolve, 100));
      const [sessionsData, participantsData] = await Promise.all([
        getSessions(),
        getParticipants()
      ]);
      setSessions(sessionsData);
      setParticipants(participantsData);
      console.log("✅ Actualisation terminée");
    } catch (err) {
      console.error("❌ Erreur actualisation:", err);
      setError('Erreur lors de l\'actualisation: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Diagnostic des données
  const handleDiagnoseData = async () => {
    console.log("🔍 Diagnostic des données en cours...");
    
    try {
      // Récupérer les données actuelles
      const currentSessions = await getSessions();
      const currentParticipants = await getParticipants();
      
      console.log("📋 Analyse des incohérences:");
      
      currentSessions.forEach(session => {
        const sessionParticipants = currentParticipants.filter(p => p.sessionId === session.id);
        
        const realParticipantCount = sessionParticipants.reduce((sum, p) => {
          const playerCount = p.playerCount && !isNaN(p.playerCount) ? parseInt(p.playerCount) : 1;
          return sum + playerCount;
        }, 0);
        
        const realPaidCount = sessionParticipants.reduce((sum, p) => {
          if (p.paid) {
            const playerCount = p.playerCount && !isNaN(p.playerCount) ? parseInt(p.playerCount) : 1;
            return sum + playerCount;
          }
          return sum;
        }, 0);
        
        if (session.participantCount !== realParticipantCount || session.paidCount !== realPaidCount) {
          console.log(`❌ Session ${session.date} ${session.time}:
            - Stocké: ${session.participantCount} participants, ${session.paidCount} payés
            - Réel: ${realParticipantCount} participants, ${realPaidCount} payés
            - Participants: ${sessionParticipants.map(p => `${p.name}(${p.playerCount || 1}joueurs,${p.paid?'payé':'non payé'})`).join(', ')}`);
        } else {
          console.log(`✅ Session ${session.date} ${session.time}: OK`);
        }
      });
      
      alert("🔍 Diagnostic terminé. Consultez la console pour les détails.");
      
    } catch (err) {
      console.error("❌ Erreur diagnostic:", err);
      alert("❌ Erreur lors du diagnostic");
    }
  };

  // Diagnostic complet des sessions
  const diagnosticCompletSessions = async () => {
    try {
      setLoading(true);
      console.log("🔍 DIAGNOSTIC COMPLET DES SESSIONS");
      const sessionsData = await getSessions();
      const participantsData = await getParticipants();
      console.log(`📊 Total sessions: ${sessionsData.length}`);
      console.log(`👥 Total participants: ${participantsData.length}`);
      const sessionsProblematiques = [];
      sessionsData.forEach(session => {
        const participantsReels = participantsData.filter(p => p.sessionId === session.id);
        const vraiNombreJoueurs = participantsReels.reduce((sum, p) => sum + (p.playerCount || 1), 0);
        const vraiNombrePayes = participantsReels.reduce((sum, p) => p.paid ? sum + (p.playerCount || 1) : sum, 0);
        const participantCountIncorrect = (session.participantCount || 0) !== vraiNombreJoueurs;
        const paidCountIncorrect = (session.paidCount || 0) !== vraiNombrePayes;
        if (participantCountIncorrect || paidCountIncorrect) {
          const sessionInfo = {
            id: session.id,
            date: session.date,
            time: session.time,
            compteurAffiché: {
              participants: session.participantCount || 0,
              payés: session.paidCount || 0
            },
            réalité: {
              participants: vraiNombreJoueurs,
              payés: vraiNombrePayes,
              nombreRéservations: participantsReels.length
            },
            participants: participantsReels.map(p => ({
              nom: p.name,
              joueurs: p.playerCount || 1,
              payé: p.paid
            }))
          };
          sessionsProblematiques.push(sessionInfo);
          console.log(`❌ Session ${session.date} ${session.time}:`);
          console.log(`   Affiché: ${session.participantCount || 0} participants, ${session.paidCount || 0} payés`);
          console.log(`   Réel: ${vraiNombreJoueurs} joueurs, ${vraiNombrePayes} payés`);
          console.log(`   Détail:`, participantsReels);
        }
      });
      console.log(`🎯 ${sessionsProblematiques.length} sessions problématiques trouvées`);
      const sessions11h = sessionsData.filter(s => s.time === '11:00');
      console.log(`📅 Sessions 11h trouvées: ${sessions11h.length}`);
      sessions11h.forEach(session => {
        const participantsReels = participantsData.filter(p => p.sessionId === session.id);
        console.log(`   ${session.date} 11h: affiché=${session.participantCount}, réel=${participantsReels.length} réservations`);
      });
      return sessionsProblematiques;
    } catch (error) {
      console.error("❌ Erreur lors du diagnostic:", error);
    } finally {
      setLoading(false);
    }
  };

  // Réparation forcée des compteurs
  const reparationForceeCompteurs = async () => {
    try {
      setLoading(true);
      console.log("🔧 RÉPARATION FORCÉE DES COMPTEURS");
      const sessionsData = await getSessions();
      const participantsData = await getParticipants();
      let compteurCorrections = 0;
      for (const session of sessionsData) {
        const participantsReels = participantsData.filter(p => p.sessionId === session.id);
        const vraiNombreJoueurs = participantsReels.reduce((sum, p) => sum + (p.playerCount || 1), 0);
        const vraiNombrePayes = participantsReels.reduce((sum, p) => p.paid ? sum + (p.playerCount || 1) : sum, 0);
        if ((session.participantCount || 0) !== vraiNombreJoueurs || 
            (session.paidCount || 0) !== vraiNombrePayes) {
          console.log(`🔧 Correction session ${session.date} ${session.time}`);
          console.log(`   Avant: ${session.participantCount || 0} participants, ${session.paidCount || 0} payés`);
          console.log(`   Après: ${vraiNombreJoueurs} participants, ${vraiNombrePayes} payés`);
          const sessionRef = doc(db, 'sessions', session.id);
          await updateDoc(sessionRef, {
            participantCount: vraiNombreJoueurs,
            paidCount: vraiNombrePayes
          });
          compteurCorrections++;
        }
      }
      console.log(`✅ ${compteurCorrections} sessions corrigées`);
      const [nouvelleSessions, nouveauxParticipants] = await Promise.all([
        getSessions(),
        getParticipants()
      ]);
      setSessions(nouvelleSessions);
      setParticipants(nouveauxParticipants);
    } catch (error) {
      console.error("❌ Erreur lors de la réparation:", error);
      setError("Erreur lors de la réparation des compteurs");
    } finally {
      setLoading(false);
    }
  };

  // Rechargement propre des données
  const rechargerDonnees = async () => {
    try {
      console.log("🔄 Rechargement des données...");
      const [nouvelleSessions, nouveauxParticipants] = await Promise.all([
        getSessions(),
        getParticipants()
      ]);
      setSessions(nouvelleSessions);
      setParticipants(nouveauxParticipants);
      console.log("✅ Données rechargées");
    } catch (error) {
      console.error("❌ Erreur lors du rechargement:", error);
    }
  };

  // Analyse spécifique des sessions 11h
  const analyserSessions11h = async () => {
    try {
      console.log("🔍 ANALYSE SPÉCIFIQUE DES SESSIONS 11H");
      const sessionsData = await getSessions();
      const participantsData = await getParticipants();
      const sessions11h = sessionsData.filter(s => s.time === '11:00');
      console.log(`📅 ${sessions11h.length} sessions 11h trouvées`);
      sessions11h.forEach(session => {
        const participantsReels = participantsData.filter(p => p.sessionId === session.id);
        const totalJoueurs = participantsReels.reduce((sum, p) => sum + (p.playerCount || 1), 0);
        console.log(`📊 ${session.date} 11h:`);
        console.log(`   Session ID: ${session.id}`);
        console.log(`   Compteur affiché: ${session.participantCount || 0}`);
        console.log(`   Participants réels: ${participantsReels.length} réservations`);
        console.log(`   Joueurs réels: ${totalJoueurs}`);
        console.log(`   Détail participants:`, participantsReels);
        if ((session.participantCount || 0) > 0 && participantsReels.length === 0) {
          console.log(`❌ PROBLÈME DÉTECTÉ: Compteur=${session.participantCount} mais aucun participant!`);
        }
      });
    } catch (error) {
      console.error("❌ Erreur lors de l'analyse:", error);
    }
  };

  // Remise à zéro forcée d'une session spécifique
  const remiseAZeroSession = async (sessionId) => {
    try {
      console.log(`🔧 Remise à zéro forcée de la session ${sessionId}`);
      const participantsData = await getParticipants();
      const participantsSession = participantsData.filter(p => p.sessionId === sessionId);
      if (participantsSession.length === 0) {
        const sessionRef = doc(db, 'sessions', sessionId);
        await updateDoc(sessionRef, {
          participantCount: 0,
          paidCount: 0
        });
        console.log("✅ Session remise à zéro");
        await rechargerDonnees();
      } else {
        console.log("❌ Impossible: la session a encore des participants:", participantsSession);
      }
    } catch (error) {
      console.error("❌ Erreur lors de la remise à zéro:", error);
    }
  };

  // Affichage d'un message de chargement pendant le chargement des données
  if (loading) {
    return <div className="loading">Chargement des données...</div>;
  }

  // Affichage d'un message d'erreur en cas d'erreur
  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <Router>
      <div className="App">
        <Header />

        {/* Boutons d'administration */}
        {!isAdmin ? (
          <button
            onClick={handleAdminClick}
            className="export-participants-button"
            style={{ position: 'absolute', top: 10, right: 10, zIndex: 1000 }}
          >
            Activer Admin
          </button>
        ) : (
          <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 1000, display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {/* Bouton d'actualisation */}
            <button
              onClick={handleForceRefresh}
              className="export-participants-button"
              style={{ background: '#06b6d4' }}
              title="Actualiser l'affichage"
              disabled={loading}
            >
              {loading ? '🔄' : '↻ Actualiser'}
            </button>
            
            {/* Bouton export CSV */}
            <button
              onClick={exportAllParticipantsCSV}
              className="export-participants-button"
              title="Exporter tous les participants en CSV"
            >
              📊 Export CSV
            </button>
            
            {/* Bouton recalcul compteurs */}
            <button
              onClick={handleRecalculateCounters}
              className="export-participants-button"
              style={{ background: '#f59e0b' }}
              title="Recalculer tous les compteurs de participants"
              disabled={loading}
            >
              {loading ? 'Calcul...' : '🔢 Recalculer'}
            </button>
            
            {/* Bouton déconnexion admin */}
            <button
              onClick={handleAdminLogout}
              className="export-participants-button"
              style={{ background: '#ef4444' }}
            >
              ❌ Désactiver Admin
            </button>
          </div>
        )}

        {/* Modal de saisie du mot de passe admin */}
        {showAdminPrompt && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
          }}>
            <form
              onSubmit={handleAdminSubmit}
              style={{
                background: '#fff', padding: 30, borderRadius: 8, boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                display: 'flex', flexDirection: 'column', gap: 15, minWidth: 300
              }}
            >
              <label htmlFor="admin-password" style={{ fontWeight: 'bold' }}>Mot de passe admin :</label>
              <input
                id="admin-password"
                type="password"
                value={adminInput}
                onChange={e => setAdminInput(e.target.value)}
                autoFocus
                style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" className="export-participants-button">Valider</button>
                <button type="button" className="export-participants-button" style={{ background: '#ef4444' }} onClick={() => setShowAdminPrompt(false)}>Annuler</button>
              </div>
            </form>
          </div>
        )}

        <main className="content">
          <Routes>
            {/* Page d'accueil - Calendrier */}
            <Route path="/" element={
              <Calendar
                currentDate={currentDate}
                sessions={sessions}
                participants={participants}  // ← AJOUTER cette ligne
                onDateChange={handleDateChange}
                getAvailableHours={getAvailableHours}
              />
            } />

            {/* Vue journalière */}
            <Route path="/daily/:dateParam" element={
              <DailyView
                currentDate={currentDate}
                sessions={sessions}
                availableHours={getAvailableHours(currentDate)}
                onSessionSelect={handleSessionSelect}
                onCreateSession={handleCreateSession}
                onDeleteSession={handleDeleteSession}
                onReopenSession={handleReopenSession}  // ← AJOUTER cette ligne
                isAdmin={isAdmin}
                isInSeason={isInSeason}
                participants={participants}
                onUnblockSession={handleUnblockSession}
              />
            } />

            {/* Détails d'une session */}
            <Route path="/session/:sessionId" element={
              selectedSession ? (
                <SessionDetails
                  session={selectedSession}
                  participants={participants.filter(p => {
                    // Trouver toutes les sessions du même créneau (date + heure)
                    const sessionTime = selectedSession.time;
                    const sessionDate = selectedSession.date;
                    const sameSlotSessions = sessions.filter(s => s.date === sessionDate && s.time === sessionTime);
                    return sameSlotSessions.some(s => s.id === p.sessionId);
                  })}
                  onUpdatePayment={handleUpdatePayment}
                  onDeleteParticipant={handleDeleteParticipant}
                  onBlockSession={handleBlockSession}
                  onUnblockSession={handleUnblockSession}
                  isAdmin={isAdmin}
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />

            {/* Ajout d'un participant */}
            <Route path="/add-participant/:sessionId" element={
              selectedSession ? (
                <AddParticipant
                  session={selectedSession}
                  onAddParticipant={handleAddParticipant}
                  onBlockSession={handleBlockSession} // ← AJOUTE cette ligne !
                  isAdmin={isAdmin}
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />
          </Routes>
        </main>

        <Footer />
      </div>
    </Router>
  );
}

export default App;